/**
 * Transform aggregate schedule query results to expected scheduleData shape
 *
 * The Supabase aggregate query returns data in format:
 * { id, crop_id, year, state, ..., crop: {...}, crop_phases: [...] }
 *
 * The timeline components expect:
 * { crop: {...}, schedule: {..., planting_date}, phases: [...] }
 */

import { parseIntervalToDays } from "~/common/utils/date-utils"

/**
 * @deprecated This function is no longer used.
 * Schedule data is now joined client-side in use-multi-crop-schedule-data.js
 * to preserve upstream crop data with all joins.
 *
 * Transform raw aggregate query result to expected scheduleData shape
 * @param {Array} rawData - Raw data from Supabase aggregate query
 * @returns {Array} Transformed scheduleData array matching useMultiCropScheduleData output
 */
export function transformAggregateScheduleData(rawData) {
  if (!rawData || rawData.length === 0) {
    return []
  }

  return rawData.map(row => {
    const { crop, crop_phases, ...scheduleFields } = row

    // Sort phases by step (should already be sorted by query, but ensure consistency)
    const sortedPhases = [...(crop_phases || [])].sort((a, b) => a.step - b.step)

    // Derive planting_date from first phase's est_started_at
    // This is needed because crop_schedules table doesn't have planting_date column
    const firstPhase = sortedPhases[0]
    const plantingDate = firstPhase?.est_started_at || null

    // Calculate expected_harvest_date from last phase's end date
    const lastPhase = sortedPhases[sortedPhases.length - 1]
    let expectedHarvestDate = null
    if (lastPhase?.est_started_at && lastPhase?.est_duration) {
      const startDate = new Date(lastPhase.est_started_at)
      const durationDays = parseIntervalToDays(lastPhase.est_duration)
      const endDate = new Date(startDate)
      endDate.setDate(endDate.getDate() + durationDays)
      expectedHarvestDate = endDate.toISOString()
    }

    return {
      crop: crop || null,
      schedule: {
        ...scheduleFields,
        planting_date: plantingDate,
        expected_harvest_date: expectedHarvestDate
      },
      phases: sortedPhases
    }
  })
}

/**
 * @deprecated This function is no longer used.
 * Client-side join in use-multi-crop-schedule-data.js now handles all crops.
 *
 * Add crops without schedules as empty rows
 * This maintains compatibility with the current behavior where all crops
 * are shown in the timeline, even if they don't have schedules yet
 *
 * @param {Array} scheduleData - Transformed schedule data
 * @param {Array} allCrops - All crops from useCropData
 * @returns {Array} Combined data with empty schedule rows
 */
export function addCropsWithoutSchedules(scheduleData, allCrops) {
  if (!allCrops || allCrops.length === 0) {
    return scheduleData
  }

  const cropsWithSchedules = new Set(
    scheduleData
      .filter(item => item.crop?.id)
      .map(item => item.crop.id)
  )

  const cropsWithoutSchedules = allCrops
    .filter(crop => !cropsWithSchedules.has(crop.id))
    .map(crop => ({
      crop,
      schedule: null,
      phases: []
    }))

  return [...scheduleData, ...cropsWithoutSchedules]
}

/**
 * Calculate expected harvest date from the last phase
 * @param {Array} phases - Array of crop phases
 * @returns {string|null} ISO date string or null
 */
export function calculateHarvestDate(phases) {
  if (!phases || phases.length === 0) return null

  const lastPhase = phases[phases.length - 1]
  if (!lastPhase?.est_started_at || !lastPhase?.est_duration) return null

  const startDate = new Date(lastPhase.est_started_at)
  const durationDays = parseIntervalToDays(lastPhase.est_duration)
  const endDate = new Date(startDate)
  endDate.setDate(endDate.getDate() + durationDays)
  return endDate.toISOString()
}

/**
 * Join crops array with schedules data via client-side left join
 * Preserves all upstream crop data (organization, templates, families)
 *
 * @param {Array} crops - Crops array from useCropData with all joins
 * @param {Array} schedulesData - Raw schedules data from getActiveByCropIds
 * @returns {Array} Combined array of { crop, schedule, phases }
 */
export function joinCropsWithSchedules(crops, schedulesData) {
  // Build a Map of crop_id -> {schedule, phases} for O(1) lookup
  const scheduleMap = new Map()
  if (schedulesData && schedulesData.length > 0) {
    schedulesData.forEach(schedule => {
      scheduleMap.set(schedule.crop_id, {
        schedule: {
          ...schedule,
          planting_date: schedule.crop_phases?.[0]?.est_started_at || null,
          expected_harvest_date: calculateHarvestDate(schedule.crop_phases)
        },
        phases: schedule.crop_phases || []
      })
    })
  }

  // Client-side left join: Map crops to schedule data
  // This preserves all upstream crop data (organization, templates, families)
  return crops.map(crop => {
    const scheduleInfo = scheduleMap.get(crop.id) || {
      schedule: null,
      phases: []
    }

    return {
      crop,  // Use upstream crop with all joins preserved
      schedule: scheduleInfo.schedule,
      phases: scheduleInfo.phases
    }
  })
}
